import logger from "logging/logger";
import { supportsSetSinkId } from "modules/deviceInfo";
import { useCallback, useEffect, useRef, useState } from "react";

const audioUrl = require("../../../micCamCheck/assets/speaker-test.mp3");

export enum AudioStates {
  LOADING = "loading",
  PAUSED = "paused",
  PLAYING = "playing",
}
export const useAudioOutputTest = () => {
  const audioTestSound = useRef(new Audio(audioUrl));
  const [audioState, setAudioState] = useState<AudioStates>(AudioStates.PAUSED);

  useEffect(() => {
    const currentAudioTestSound = audioTestSound.current;

    currentAudioTestSound.pause();

    const handlePlay = () => {
      setAudioState(AudioStates.PLAYING);
    };

    const handlePause = () => {
      // seek to beginning
      currentAudioTestSound.currentTime = 0;
      setAudioState(AudioStates.PAUSED);
    };

    const audioHandlers = {
      playing: handlePlay,
      end: handlePause,
      pause: handlePause,
    };

    Object.entries(audioHandlers).forEach(([event, handler]) => {
      currentAudioTestSound.addEventListener(event, handler);
    });

    return () => {
      Object.entries(audioHandlers).forEach(([event, handler]) => {
        currentAudioTestSound.removeEventListener(event, handler);
      });
    };
  }, []);

  const playTestSound = useCallback(() => {
    audioTestSound.current.play();
  }, []);

  const pauseTestSound = useCallback(() => {
    audioTestSound.current.pause();
  }, []);

  const setAudioOutput = useCallback(async (deviceId: string) => {
    if (!supportsSetSinkId()) {
      logger.warn(`[audioOutputTest] setSinkId not supported`);
      return;
    }
    try {
      await audioTestSound.current.setSinkId?.(deviceId);
    } catch (error) {
      if (error instanceof Error) {
        logger.error(error.message, error);
      }
    }
  }, []);

  const toggleTestSound = useCallback(() => {
    switch (audioState) {
      case AudioStates.PLAYING: {
        pauseTestSound();
        break;
      }
      case AudioStates.PAUSED: {
        playTestSound();
        break;
      }
      default: {
        break;
      }
    }
  }, [pauseTestSound, playTestSound, audioState]);

  return {
    audioState,
    playTestSound,
    pauseTestSound,
    toggleTestSound,
    setAudioOutput,
  };
};
